import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable, inject, signal } from '@angular/core';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { Tenant } from "src/app/core/models/tenant";
import { BaseTenantService } from "src/app/core/services/base-tenant.service";
import { environment } from "src/environments/environment";
import { Mongo } from '../../core/models/mongo';

@Injectable({
  providedIn: "any",
})
export class MgmtTenantService extends BaseTenantService {
  getAll(): Observable<Tenant.Tenant[]> {
    const tenants: Tenant.Tenant[] = [];
    let page = 1;

    return new Observable<Tenant.Tenant[]>(observer => {
      const fetchPage = () => {
        this.http.get<Tenant.Tenant[]>(
          `${this.MGMT_BASE_URL}?sort={"_id":1}&page=${page}`
        ).subscribe({
          next: (result) => {
            if (result.length === 0) {
              observer.next(tenants);
              observer.complete();
            } else {
              tenants.push(...result);
              page++;
              fetchPage(); // Fetch the next page
            }
          },
          error: (err) => observer.error(err)
        });
      };

      fetchPage(); // Start fetching the first page
    });
  }

  public getPage(page = 1, pageSize = 15, filter?: string): Observable<Tenant.Tenant[]> {
    const queryParams = filter !== ""
        ? `pagesize=${pageSize}&page=${page}&${filter}&sort={"_id":1}`
        : `pagesize=${pageSize}&page=${page}&sort={"_id":1}`;
    return this.http.get<Tenant.Tenant[]>(`${this.MGMT_BASE_URL}?${queryParams}`);
  }

  public getCollectionSize(filter?: string): Observable<number> {
    const queryParams = filter ? `?${filter}` : "";
    return this.http
      .get<{ _size: number }>(`${this.MGMT_BASE_URL}/_size${queryParams}`)
      .pipe(map((resp: { _size: number }) => resp._size));
  }

  /**
   * On creation, the backend will correctly initialize all needed collections
   */
  public store(tenant: Tenant.Tenant): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(
      `${this.BASE_URL}/initTenant/${tenant._id}`,
      tenant,
      {
        observe: "response",
      }
    );
  }

  public stats(tenantId: string): Observable<Tenant.Stats> {
    const actions= this.http.get<{_size: number}>(`${this.BASE_URL}/${tenantId}/plannings/_size?filter={"draft":{"$exists":false}}`).pipe(map(s => s._size));
    const reports= this.http.get<{_size: number}>(`${this.BASE_URL}/${tenantId}/reports/_size`).pipe(map(s => s._size));;
    const lastReport= this.http.get<[{_id: Mongo.ObjectId, year: number}]>(`${this.BASE_URL}/${tenantId}/reports?pagesize=1&keys={"_id":1,"year":1}`).pipe(map(lrs => lrs.length < 1 ? null : lrs[0]));
    const activeUsers = this.http.get<{_size: number}>(`${this.BASE_URL}/mgmt/users/_size?filter={"$and":[ {"tenants":"${encodeURIComponent(tenantId)}"}, {"$or":[{"roles":"user"},{"roles":"manager"}]}]}`).pipe(map(s => s._size));;

    return combineLatest([actions, reports, activeUsers, lastReport]).pipe(
      map(([actions, reports, activeUsers, lastReportData]) => ({
        tenantId,
        actions,
        reports,
        activeUsers,
        lastReport: lastReportData ? {
          id: lastReportData._id,
          year: lastReportData.year
        } : null
      })));
  }
}
